import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import PropTypes from 'prop-types';

const ProductCard = ({ product, onAddToCart, viewMode = 'grid' }) => {
  // Get first image URL safely
  const getImageUrl = () => {
    try {
      // Check if images exist and have at least one item with url
      if (!product?.images?.length || !product.images[0]?.url) {
        throw new Error('No valid image found');
      }

      // Validate URL structure
      new URL(product.images[0].url);
      return product.images[0].url;
    } catch (error) {
      console.warn('Image error:', error.message);
      return '/placeholder-product.jpg';
    }
  };

  const imageUrl = getImageUrl();
  const imageAlt = product.images?.[0]?.alt || product.name;

  // Handle add to cart with event prevention
  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAddToCart();
  };

  if (viewMode === 'list') {
    return (
      <Link 
        to={`/product/${product._id}`} 
        className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 flex group h-full"
      >
        {/* Product Image */}
        <div className="flex-shrink-0 w-40 h-40 relative">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/placeholder-product.jpg';
            }}
            loading="lazy"
          />
          {product.discountPercentage > 0 && (
            <span className="absolute top-2 right-2 bg-pinkAccent text-white text-xs font-bold px-2 py-1 rounded-full">
              -{product.discountPercentage}%
            </span>
          )}
        </div>

        {/* Product Info */}
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 group-hover:text-pinkAccent transition-colors">
              {product.name}
            </h3>
            <p className="text-sm text-gray-500 mt-1 capitalize">{product.category}</p>
            {product.description && (
              <p className="text-gray-700 mt-2 text-sm line-clamp-2">
                {product.description}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gray-900">
                ${product.price?.toFixed(2)}
              </span>
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="text-sm text-gray-400 line-through">
                  ${product.originalPrice?.toFixed(2)}
                </span>
              )}
            </div>
            <Button
              onClick={handleAddToCart}
              variant="primary"
              size="small"
              className="ml-4"
              disabled={!product.isAvailable}
            >
              {product.isAvailable ? 'Add to Cart' : 'Out of Stock'}
            </Button>
          </div>
        </div>
      </Link>
    );
  }

  // Default grid view
  return (
    <Link 
      to={`/product/${product._id}`} 
      className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 block group h-full flex flex-col"
    >
      {/* Product Image */}
      <div className="aspect-square bg-gray-100 relative">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = '/placeholder-product.jpg';
          }}
          loading="lazy"
        />
        {product.discountPercentage > 0 && (
          <span className="absolute top-2 right-2 bg-pinkAccent text-white text-xs font-bold px-2 py-1 rounded-full">
            -{product.discountPercentage}%
          </span>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-lg font-medium text-gray-900 group-hover:text-pinkAccent transition-colors">
          {product.name}
        </h3>
        <p className="text-sm text-gray-500 mt-1 capitalize">{product.category}</p>
        
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900">
              ${product.price?.toFixed(2)}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-sm text-gray-400 line-through">
                ${product.originalPrice?.toFixed(2)}
              </span>
            )}
          </div>
          {product.rating && (
            <span className="flex items-center text-sm text-gray-500">
              ★ {product.rating.average?.toFixed(1) || '0.0'}
            </span>
          )}
        </div>

        <Button
          onClick={handleAddToCart}
          variant="primary"
          size="small"
          className="w-full mt-4"
          disabled={!product.isAvailable}
        >
          {product.isAvailable ? 'Add to Cart' : 'Out of Stock'}
        </Button>
      </div>
    </Link>
  );
};

ProductCard.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    originalPrice: PropTypes.number,
    category: PropTypes.string,
    description: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
        _id: PropTypes.string
      })
    ),
    rating: PropTypes.shape({
      average: PropTypes.number,
      count: PropTypes.number
    }),
    discountPercentage: PropTypes.number,
    isAvailable: PropTypes.bool,
    stock: PropTypes.number
  }).isRequired,
  onAddToCart: PropTypes.func.isRequired,
  viewMode: PropTypes.oneOf(['grid', 'list'])
};

export default ProductCard;